/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/12/22, 10:08 AM
 *
 */

import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { CaretDown, CaretUp, WarningOctagon } from 'phosphor-react';

import { useInvoiceHistory } from 'features/members/api/query/useInvoiceHistory';
import { Invoice } from 'features/members/types';

import { Badge } from '@sunyahealthnepal/react-ui-components';
import Loader from 'components/Loader';

interface IPropsInvoiceHistory {
  member_id: number;
}

export const InvoiceHistory = ({ member_id }: IPropsInvoiceHistory) => {
  const { data: invoiceList, isLoading } = useInvoiceHistory(member_id, 1);

  if (isLoading) return <Loader />;

  return invoiceList && invoiceList.length !== 0 ? (
    <div className="flex w-full flex-col space-y-4 self-start print:hidden">
      <span className="text-2xl font-medium text-gray-900">Invoice History</span>
      <div className="divide-y-[1px] divide-gray-400/40">
        {invoiceList.map((invoice, index) => {
          if (index > 2) return;

          return (
            <Fragment key={invoice.invoice_no}>
              <InvoiceHistoryItem invoice={invoice} />
            </Fragment>
          );
        })}
      </div>
      {invoiceList.length > 3 ? (
        <Disclosure className="w-full" as="div">
          {({ open }) => (
            <div
              className={`${
                !open ? 'flex flex-col' : '-my-4 flex flex-col-reverse border-t border-gray-400/40'
              }`}
            >
              <Disclosure.Button className="w-full">
                <div
                  className={`flex w-full items-center justify-between border-t border-gray-400/60 py-4 ${
                    !open && '-my-4'
                  }`}
                >
                  <span className="text-lg font-medium text-gray-700">
                    {!open ? 'Show More' : 'Show Less'}
                  </span>
                  <span className="rounded-full bg-primary-400 p-2 text-white">
                    {!open ? (
                      <CaretDown size={14} weight="bold" />
                    ) : (
                      <CaretUp size={14} weight="bold" />
                    )}
                  </span>
                </div>
              </Disclosure.Button>
              <Disclosure.Panel className="divide-y-[1px] divide-gray-400/40">
                {invoiceList.map((invoice, index) => {
                  if (index < 2) return;

                  return (
                    <Fragment key={invoice.invoice_no}>
                      <InvoiceHistoryItem invoice={invoice} />
                    </Fragment>
                  );
                })}
              </Disclosure.Panel>{' '}
            </div>
          )}
        </Disclosure>
      ) : null}
    </div>
  ) : (
    <div className="flex w-full flex-col space-y-4 self-start print:hidden">
      <span className="text-2xl font-medium text-gray-900">Invoice History</span>
      <div className="flex items-center space-x-4 text-red-500 print:hidden">
        <WarningOctagon size={40} />
        <span className="text-xl font-medium">No Invoices Found</span>
      </div>
    </div>
  );
};

const InvoiceHistoryItem = ({ invoice }: { invoice: Invoice }) => {
  const navigate = useNavigate();

  return (
    <div className="flex w-full items-center justify-between py-3">
      <div
        className="flex cursor-pointer flex-col"
        onClick={() => {
          navigate(`/profile/invoice?id=${invoice.invoice_no}`);
        }}
      >
        <span className="text-xl font-medium text-gray-700">INV-0{invoice.invoice_no}</span>
        <span className="text-base font-medium text-gray-500">
          {invoice.subscription_detail.plan.name}
        </span>
      </div>
      <div>
        <Badge
          type="error"
          condition={!!invoice.paid}
          trueStatement="PAID"
          falseStatement="NOT PAID"
        />
      </div>
    </div>
  );
};
